import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from '@/components/ui/sonner'
import api from '@/lib/api'
import { ArrowLeft, CheckCircle2, FileText, Loader2 } from 'lucide-react'

const PLATFORM_CONFIG: Record<
  string,
  { label: string; type: 'code' | 'attestation'; placeholder?: string; helpText: string }
> = {
  tiktok: {
    label: 'TikTok',
    type: 'code',
    placeholder: 'Paste your TikTok Spark Ads authorization code...',
    helpText: 'Go to Settings → Creator Tools → Spark Ads to generate your code',
  },
  instagram: {
    label: 'Instagram',
    type: 'code',
    placeholder: 'Paste your Instagram branded content code...',
    helpText: 'Go to Settings → Business → Branded Content to generate your code',
  },
  youtube: {
    label: 'YouTube',
    type: 'attestation',
    helpText: 'Confirm you have enabled branded content permissions in YouTube Studio',
  },
}

interface PlatformRequirement {
  platform: string
  submission_type: 'code' | 'attestation'
  status: 'pending' | 'completed'
  submitted_value?: string | boolean
}

interface MilestoneMetadata {
  step?: string
  platforms?: PlatformRequirement[]
}

interface Milestone {
  id: string
  name: string
  status: string
  metadata?: MilestoneMetadata
}

interface LicensingOffer {
  platforms: Array<{
    platform: string
    usage_permission: string[]
    territory: string
    usage_term_days: number
    fee: number
  }>
  total_amount: number
  message?: string
}

interface Campaign {
  id: string
  title: string
  milestones: Milestone[]
  offerDetails?: {
    licensing_offer?: LicensingOffer
  }
}

const LicensingPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [platformInputs, setPlatformInputs] = useState<Record<string, string | boolean>>({})

  const fetchCampaign = useCallback(async () => {
    try {
      setLoading(true)
      const response = await api.get(`/athlete/collaborations/${id}`)
      setCampaign(response.data)
    } catch (error) {
      toast.error('Error', { description: 'Failed to load collaboration details' })
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchCampaign()
  }, [fetchCampaign])

  const handleApprove = async () => {
    try {
      setSubmitting(true)
      await api.post(`/athlete/collaborations/${id}/approve-licensing`)
      toast('Approved', { description: 'Please submit your platform codes below.' })
      fetchCampaign()
    } catch (error) {
      toast.error('Error', { description: 'Failed to approve licensing' })
    } finally {
      setSubmitting(false)
    }
  }

  // Submit a single pre-configured platform (one at a time)
  const handleSubmitPlatform = async (platform: string, milestoneId: string) => {
    const value = platformInputs[platform]
    const config = PLATFORM_CONFIG[platform]

    if (config?.type === 'code' && (!value || typeof value !== 'string' || !value.trim())) {
      toast.error('Error', { description: `Please enter the ${config.label} code` })
      return
    }
    if (config?.type === 'attestation' && value !== true) {
      toast.error('Error', { description: `Please confirm the ${config?.label} checkbox` })
      return
    }

    try {
      setSubmitting(true)
      const response = await api.post(
        `/athlete/collaborations/${id}/milestones/${milestoneId}/submit-platform-licensing`,
        { platform, value },
      )
      const result = response.data
      toast('Submitted', {
        description: result.allCompleted
          ? 'All platforms submitted! Licensing complete.'
          : `${config?.label} submitted. ${result.remainingPlatforms.length} platform(s) remaining.`,
      })
      if (result.allCompleted) {
        navigate(`/collaborations/${id}`)
      } else {
        fetchCampaign()
      }
    } catch (error: any) {
      toast.error('Error', { description: error.response?.data?.error || 'Failed to submit' })
    } finally {
      setSubmitting(false)
    }
  }

  // Submit all platforms at once (used when no platforms are pre-configured)
  const handleSubmitAll = async (milestoneId: string) => {
    const toSubmit: Array<{ platform: string; value: string | boolean }> = []
    for (const [platform, config] of Object.entries(PLATFORM_CONFIG)) {
      const value = platformInputs[platform]
      if (config.type === 'code' && typeof value === 'string' && value.trim()) {
        toSubmit.push({ platform, value: value.trim() })
      } else if (config.type === 'attestation' && value === true) {
        toSubmit.push({ platform, value: true })
      }
    }

    if (toSubmit.length === 0) {
      toast.error('Input Required', {
        description: 'Please fill in at least one platform code or confirmation',
      })
      return
    }

    try {
      setSubmitting(true)
      for (const { platform, value } of toSubmit) {
        await api.post(
          `/athlete/collaborations/${id}/milestones/${milestoneId}/submit-platform-licensing`,
          { platform, value },
        )
      }
      toast('Success', { description: 'Platform codes submitted.' })
      navigate(`/collaborations/${id}`)
    } catch (error: any) {
      toast.error('Error', {
        description: error.response?.data?.error || 'Failed to submit platform codes',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C0FD72]" />
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <p className="text-gray-300">Collaboration not found</p>
      </div>
    )
  }

  const licensingMilestone = campaign.milestones.find(
    (m) => m.name === 'License Content' || m.name === 'Licensing Rights',
  )

  if (!licensingMilestone) {
    return (
      <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-gray-900 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6 text-white hover:text-[#C0FD72] hover:bg-white/10"
            onClick={() => navigate(`/collaborations/${id}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Collaboration
          </Button>
          <Card className="bg-gray-900/90 border border-white/10">
            <CardContent className="pt-6">
              <p className="text-center text-gray-400">
                Licensing is not enabled for this collaboration.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const metadata = licensingMilestone.metadata || {}
  const platforms = metadata.platforms || []
  const licensingOffer = campaign.offerDetails?.licensing_offer

  const isPendingApproval =
    licensingMilestone.status === 'pending' && metadata.step === 'pending_approval'
  const isPendingSubmission =
    metadata.step === 'ad_code_submission' || licensingMilestone.status === 'in_progress'
  const isCompleted =
    licensingMilestone.status === 'approved' ||
    licensingMilestone.status === 'completed' ||
    licensingMilestone.status === 'submitted'

  const pendingPlatforms = platforms.filter((p) => p.status === 'pending')
  const completedPlatforms = platforms.filter((p) => p.status === 'completed')
  const hasPreConfiguredPlatforms = platforms.length > 0

  // Platforms to show in freeform submission — constrained to the agreed offer, else all known platforms
  const submissionPlatforms =
    licensingOffer?.platforms.map((p) => p.platform) ?? Object.keys(PLATFORM_CONFIG)

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-gray-900 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-white hover:text-[#C0FD72] hover:bg-white/10"
          onClick={() => navigate(`/collaborations/${id}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Collaboration
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-1">Content Licensing</h1>
          <p className="text-gray-400">{campaign.title}</p>
        </div>

        {/* Offer Summary */}
        {licensingOffer && (
          <Card className="mb-6 bg-gray-900/90 backdrop-blur-xs border border-white/10">
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-lg text-white">Licensing Offer Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {licensingOffer.platforms.map((p, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center border-b border-white/10 pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="font-medium text-white">
                      {PLATFORM_CONFIG[p.platform]?.label || p.platform}
                    </p>
                    <p className="text-sm text-gray-400">
                      {p.territory} · {p.usage_term_days} days
                      {p.usage_permission?.length > 0 && ` · ${p.usage_permission.join(', ')}`}
                    </p>
                  </div>
                  {p.fee > 0 && (
                    <p className="font-semibold text-[#C0FD72]">${p.fee.toFixed(2)}</p>
                  )}
                </div>
              ))}
              {licensingOffer.total_amount > 0 && (
                <div className="flex justify-between items-center pt-2 border-t border-white/10 font-semibold">
                  <span className="text-white">Total Amount</span>
                  <span className="text-[#C0FD72]">${licensingOffer.total_amount.toFixed(2)}</span>
                </div>
              )}
              {licensingOffer.message && (
                <div className="bg-white/5 p-3 rounded-md text-sm">
                  <p className="text-gray-400 italic">"{licensingOffer.message}"</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Action Card */}
        <Card className="bg-gray-900/90 backdrop-blur-xs border border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="text-white flex items-center gap-2">
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 text-[#C0FD72]" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-gray-600" />
              )}
              {isPendingApproval ? 'Approve Licensing Request' : 'Submit Platform Codes'}
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Pending Approval */}
            {isPendingApproval && (
              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-sm">
                  <p className="text-blue-300">
                    <strong>What is this?</strong> By approving, you agree to let the brand use
                    your handle and content to run paid advertisements on the platforms listed
                    above.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-amber-300 bg-amber-500/10 p-3 rounded-md border border-amber-500/20">
                  <FileText className="h-4 w-4 shrink-0" />
                  <span className="text-sm">Action Required: Approve Request</span>
                </div>
                <Button
                  variant="lime"
                  className="w-full"
                  size="lg"
                  onClick={handleApprove}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" /> Approve Licensing Request
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Pending Submission */}
            {isPendingSubmission && (
              <div className="space-y-5">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <p className="text-blue-300 text-sm">
                    Provide the authorization code(s) for each platform where you'll be posting.
                    {!hasPreConfiguredPlatforms && ' Fill in at least one.'}
                  </p>
                </div>

                {/* Pre-configured platforms (from marketer licensing offer) */}
                {hasPreConfiguredPlatforms && (
                  <div className="space-y-4">
                    {/* Already submitted */}
                    {completedPlatforms.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-[#C0FD72]">Submitted:</p>
                        {completedPlatforms.map((p) => (
                          <div
                            key={p.platform}
                            className="flex items-center gap-2 p-3 bg-[#C0FD72]/10 rounded-md border border-[#C0FD72]/30"
                          >
                            <CheckCircle2 className="h-4 w-4 text-[#C0FD72]" />
                            <span className="text-[#C0FD72]">
                              {PLATFORM_CONFIG[p.platform]?.label || p.platform}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pending platforms */}
                    {pendingPlatforms.map((p) => {
                      const config = PLATFORM_CONFIG[p.platform]
                      if (!config) return null
                      return (
                        <div
                          key={p.platform}
                          className="bg-gray-800/50 border border-white/10 rounded-lg p-4 space-y-3"
                        >
                          <p className="font-medium text-white">{config.label}</p>
                          {config.type === 'code' && (
                            <div className="space-y-2">
                              <Input
                                placeholder={config.placeholder}
                                value={(platformInputs[p.platform] as string) || ''}
                                onChange={(e) =>
                                  setPlatformInputs((prev) => ({
                                    ...prev,
                                    [p.platform]: e.target.value,
                                  }))
                                }
                                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                              />
                              <p className="text-xs text-gray-400">{config.helpText}</p>
                            </div>
                          )}
                          {config.type === 'attestation' && (
                            <div className="flex items-start gap-3">
                              <Checkbox
                                id={`attest-${p.platform}`}
                                checked={(platformInputs[p.platform] as boolean) || false}
                                onCheckedChange={(checked) =>
                                  setPlatformInputs((prev) => ({ ...prev, [p.platform]: checked }))
                                }
                              />
                              <label
                                htmlFor={`attest-${p.platform}`}
                                className="text-sm text-gray-300"
                              >
                                {config.helpText}
                              </label>
                            </div>
                          )}
                          <Button
                            variant="lime"
                            className="w-full"
                            onClick={() => handleSubmitPlatform(p.platform, licensingMilestone.id)}
                            disabled={submitting}
                          >
                            {submitting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                              </>
                            ) : (
                              `Submit ${config.label}`
                            )}
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* No pre-configured platforms — show inputs for agreed platforms */}
                {!hasPreConfiguredPlatforms && (
                  <div className="space-y-4">
                    {submissionPlatforms.map((platform) => {
                      const config = PLATFORM_CONFIG[platform]
                      if (!config) return null
                      return (
                        <div
                          key={platform}
                          className="bg-gray-800/50 border border-white/10 rounded-lg p-4 space-y-3"
                        >
                          <p className="font-medium text-white">{config.label}</p>
                          {config.type === 'code' ? (
                            <div className="space-y-2">
                              <Input
                                placeholder={config.placeholder}
                                value={(platformInputs[platform] as string) || ''}
                                onChange={(e) =>
                                  setPlatformInputs((prev) => ({
                                    ...prev,
                                    [platform]: e.target.value,
                                  }))
                                }
                                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                              />
                              <p className="text-xs text-gray-400">{config.helpText}</p>
                            </div>
                          ) : (
                            <div className="flex items-start gap-3">
                              <Checkbox
                                id={`attest-${platform}`}
                                checked={(platformInputs[platform] as boolean) || false}
                                onCheckedChange={(checked) =>
                                  setPlatformInputs((prev) => ({ ...prev, [platform]: checked }))
                                }
                              />
                              <label
                                htmlFor={`attest-${platform}`}
                                className="text-sm text-gray-300"
                              >
                                {config.helpText}
                              </label>
                            </div>
                          )}
                        </div>
                      )
                    })}

                    <Button
                      variant="lime"
                      className="w-full"
                      size="lg"
                      onClick={() => handleSubmitAll(licensingMilestone.id)}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                        </>
                      ) : (
                        'Submit Platform Codes'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Completed */}
            {isCompleted && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle2 className="h-16 w-16 text-[#C0FD72] mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Licensing Complete</h3>
                <p className="text-gray-400">All platform codes have been submitted.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default LicensingPage
